"use server";

import { prisma } from "@/lib/prisma";
import { dbg } from "@/lib/debug-log";
import crypto from "crypto";

const OTP_PREFIX = "otp:";
const OTP_EXPIRY_MINUTES = 10;

// ── Helpers ────────────────────────────────────────────────────────────────

function hashCode(code: string): string {
  return crypto.createHash("sha256").update(code).digest("hex");
}

function otpIdentifier(email: string): string {
  return `${OTP_PREFIX}${email.toLowerCase()}`;
}

// ── isInvited ──────────────────────────────────────────────────────────────
// Cheap guest-list pre-check for the browser magic-link flow. The magic-link
// path goes through NextAuth's signIn(), whose thrown errors get wrapped into
// opaque codes that don't reliably surface as NOT_INVITED — so the UI showed a
// generic "something went wrong" instead of the Request Access prompt. Calling
// this BEFORE signIn() lets the client branch deterministically, exactly like
// the OTP path does with sendOTP's return value. Backend gating is unchanged:
// sendVerificationRequest and the signIn callback in lib/auth.ts still block
// uninvited emails on their own — this only fixes which message the user sees.

export async function isInvited(email: string): Promise<{ invited: boolean }> {
  const user = await prisma.user.findUnique({
    where: { email: email.toLowerCase() },
    select: { id: true },
  });
  const invited = !!user;
  dbg.info("otp:isInvited", invited ? "on guest list" : "NOT on guest list", { email });
  return { invited };
}

// ── sendOTP ────────────────────────────────────────────────────────────────
// Generates a 6-digit code, stores it hashed in VerificationToken, sends email.
// Safe to call multiple times — deletes any existing OTP for this email first.
// Returns { ok: false, error: "NOT_INVITED" } if the email has no User row,
// so the login page can show the "not on the guest list" UI.

export async function sendOTP(email: string): Promise<{ ok: boolean; error?: string }> {
  dbg.info("otp:sendOTP", "called", { email });

  try {
    // ── Guest-list check ──────────────────────────────────────────────────
    // Only send OTP codes to emails that already have a User row.
    const user = await prisma.user.findUnique({ where: { email: email.toLowerCase() } });
    if (!user) {
      dbg.warn("otp:sendOTP", "blocked — not on guest list", { email });
      return { ok: false, error: "NOT_INVITED" };
    }

    // Generate 6-digit code
    const code = String(Math.floor(100000 + crypto.getRandomValues(new Uint32Array(1))[0] % 900000)).padStart(6, "0");
    const hashed = hashCode(code);
    const identifier = otpIdentifier(email);
    const expires = new Date(Date.now() + OTP_EXPIRY_MINUTES * 60 * 1000);

    dbg.info("otp:sendOTP", "generated code", { email, expires });

    // Delete any existing OTP for this email (upsert not available on compound key)
    await prisma.verificationToken.deleteMany({ where: { identifier } });

    // Store hashed code
    await prisma.verificationToken.create({
      data: { identifier, token: hashed, expires },
    });

    // Send email
    const nodemailer = await import("nodemailer");
    const transport = nodemailer.createTransport({
      host: process.env.EMAIL_SERVER_HOST,
      port: Number(process.env.EMAIL_SERVER_PORT),
      auth: {
        user: process.env.EMAIL_SERVER_USER,
        pass: process.env.EMAIL_SERVER_PASSWORD,
      },
    });

    // Pull site identity for the email template
    const [siteNameRow, placeholderRow, dueDateRow] = await Promise.all([
      prisma.siteConfig.findUnique({ where: { key: "site_name" } }),
      prisma.siteConfig.findUnique({ where: { key: "baby_name_placeholder" } }),
      prisma.siteConfig.findUnique({ where: { key: "due_date" } }),
    ]);
    const siteName    = siteNameRow?.value    ?? "Baby Name Championship";
    const placeholder = placeholderRow?.value ?? "Baby";
    const dueDateRaw  = dueDateRow?.value;
    let subtitle = "";
    if (dueDateRaw) {
      try {
        const d = new Date(dueDateRaw + "T12:00:00");
        subtitle = `${placeholder} • Due ${d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`;
      } catch {
        subtitle = placeholder;
      }
    }

    await transport.sendMail({
      to: email,
      from: process.env.EMAIL_FROM,
      subject: `🏆 Your ${siteName} Sign-In Code`,
      text: [
        `Sign in to ${siteName}`,
        "",
        `Your sign-in code is: ${code}`,
        "",
        "Enter this code in the app to sign in. It expires in 10 minutes.",
        "",
        "If you didn't request this, you can ignore it.",
      ].join("\n"),
      html: `
        <div style="font-family: sans-serif; max-width: 480px; margin: 0 auto; padding: 32px; background: linear-gradient(135deg, #f3e8ff, #fce7f3); border-radius: 16px;">
          <h1 style="color: #6b21a8; font-size: 28px; margin-bottom: 8px; text-align: center;">🏆</h1>
          <h2 style="color: #6b21a8; text-align: center; margin-bottom: 4px;">${siteName}</h2>
          ${subtitle ? `<p style="color: #7e22ce; text-align: center; margin-bottom: 32px; font-size: 14px;">${subtitle}</p>` : `<div style="margin-bottom: 32px;"></div>`}
          <p style="color: #581c87; margin-bottom: 16px; text-align: center;">Your sign-in code is:</p>
          <div style="background: #6b21a8; border-radius: 16px; padding: 24px; text-align: center; margin-bottom: 24px; letter-spacing: 0.25em;">
            <span style="color: white; font-size: 42px; font-weight: 900; font-family: monospace;">${code}</span>
          </div>
          <p style="color: #7e22ce; text-align: center; font-size: 14px; margin-bottom: 8px;">
            Enter this code in the app to sign in.
          </p>
          <p style="color: #9333ea; text-align: center; font-size: 13px; margin-bottom: 24px;">
            ⏱ Expires in 10 minutes
          </p>
          <p style="color: #9333ea; font-size: 12px; text-align: center;">If you didn't request this, you can safely ignore this email.</p>
        </div>
      `,
    });

    dbg.info("otp:sendOTP", "email sent", { email });
    return { ok: true };
  } catch (err) {
    dbg.error("otp:sendOTP", "failed", { email, err });
    console.error("[otp:sendOTP]", err);
    return { ok: false, error: "Failed to send code. Please try again." };
  }
}

// ── verifyOTP ──────────────────────────────────────────────────────────────
// Checks the code against the stored hash. Returns the user's email on success
// so the caller can complete sign-in via signIn("nodemailer", ...) or a
// credentials flow. Deletes the token on success (single-use).

export async function verifyOTP(
  email: string,
  code: string
): Promise<{ ok: boolean; error?: string }> {
  dbg.info("otp:verifyOTP", "called", { email, codeLength: code.length });

  try {
    const identifier = otpIdentifier(email.toLowerCase());
    const hashed = hashCode(code.trim());

    const record = await prisma.verificationToken.findFirst({
      where: { identifier, token: hashed },
    });

    if (!record) {
      dbg.warn("otp:verifyOTP", "no matching token", { email });
      return { ok: false, error: "Invalid code. Please check and try again." };
    }

    if (record.expires < new Date()) {
      dbg.warn("otp:verifyOTP", "expired", { email, expires: record.expires });
      await prisma.verificationToken.deleteMany({ where: { identifier } });
      return { ok: false, error: "Code expired. Please request a new one." };
    }

    // Valid — delete so it can't be reused
    await prisma.verificationToken.deleteMany({ where: { identifier } });

    dbg.info("otp:verifyOTP", "success", { email });
    return { ok: true };
  } catch (err) {
    dbg.error("otp:verifyOTP", "failed", { email, err });
    console.error("[otp:verifyOTP]", err);
    return { ok: false, error: "Something went wrong. Please try again." };
  }
}
